// ── 공동구매 (Group Buying) 유틸리티 ──
// 셈핏 운영자가 큐레이션한 공동구매 기회를 관리하고
// 사용자가 참여 의향을 등록하는 모듈.
//
// Firestore 구조:
//   /groupBuyings/{id}            - 전역 공동구매 카드 (운영자 작성)
//   /groupBuyingParticipants/{id} - 사용자 참여 기록 (userId 별)

// 공동구매 카드 상태
export const GB_STATUS = {
  open: { label: '모집 중', color: '#18181B', bg: '#F4F4F5' },
  closing: { label: '마감 임박', color: '#52525B', bg: '#F4F4F5' },
  closed: { label: '마감', color: '#6b7280', bg: '#f3f4f6' },
  fulfilled: { label: '배송 완료', color: '#0369a1', bg: '#dbeafe' },
};

// 참여 상태
export const PARTICIPANT_STATUS = {
  interested: { label: '관심', color: '#A1A1AA' },
  confirmed: { label: '확정', color: '#18181B' },
  cancelled: { label: '취소', color: '#9ca3af' },
};

// 빈 공동구매 템플릿 (운영자 등록 시)
export const emptyGroupBuying = () => ({
  title: '',
  productName: '',
  category: '',          // 야채/육류/수산/공산품/기타
  unit: '',              // 예: 10kg, 1박스, 100개
  pricePerUnit: 0,       // 공동구매가
  marketPrice: 0,        // 시세 (KAMIS/네이버 기준)
  supplier: '',          // 공급처
  minOrder: 1,           // 최소 모집 수량
  maxOrder: null,        // 최대 (null=무제한)
  deadline: '',          // YYYY-MM-DD 마감일
  pickupDate: '',        // 배송/픽업 예정일
  pickupLocation: '',    // 픽업 위치 (또는 "택배 배송")
  description: '',       // 운영자 메모
  status: 'open',
  createdAt: null,
  createdBy: '',         // 운영자 uid
});

// 빈 참여 템플릿
export const emptyParticipant = (groupBuyingId, user) => ({
  groupBuyingId,
  userId: user?.uid || '',
  userName: user?.displayName || user?.email?.split('@')[0] || '',
  userEmail: user?.email || '',
  phone: '',
  quantity: 1,
  memo: '',
  status: 'interested',
  createdAt: null,
});

// 절감액 계산
export function calcSavings(gb) {
  const market = Number(gb.marketPrice) || 0;
  const price = Number(gb.pricePerUnit) || 0;
  if (!market || !price) return { amount: 0, percent: 0 };
  const amount = market - price;
  const percent = market > 0 ? Math.round((amount / market) * 100) : 0;
  return { amount, percent };
}

// 마감까지 남은 일수
export function daysUntilDeadline(deadline) {
  if (!deadline) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const d = new Date(deadline);
  d.setHours(0, 0, 0, 0);
  const diff = Math.round((d - now) / (1000 * 60 * 60 * 24));
  return diff;
}

// 마감 임박 여부 (3일 이내)
export function isClosingSoon(gb) {
  if (gb.status !== 'open') return false;
  const days = daysUntilDeadline(gb.deadline);
  return days !== null && days >= 0 && days <= 3;
}

// 자동 상태 계산 (DB status가 open이어도 마감일 지났으면 closed로 표시)
export function effectiveStatus(gb) {
  if (gb.status !== 'open') return gb.status;
  const days = daysUntilDeadline(gb.deadline);
  if (days !== null && days < 0) return 'closed';
  if (days !== null && days <= 3) return 'closing';
  return 'open';
}

// 정렬: 마감 임박 우선 → 절감액 큰 순
export function sortGroupBuyings(list) {
  const arr = Array.isArray(list) ? [...list] : [];
  return arr.sort((a, b) => {
    const sa = effectiveStatus(a);
    const sb = effectiveStatus(b);
    // 진행 중인 것 (open/closing) 먼저
    const rank = { closing: 0, open: 1, closed: 2, fulfilled: 3 };
    if (rank[sa] !== rank[sb]) return rank[sa] - rank[sb];
    // 같은 상태 안에서: 마감일 가까운 순
    const da = daysUntilDeadline(a.deadline) ?? 9999;
    const db = daysUntilDeadline(b.deadline) ?? 9999;
    if (da !== db) return da - db;
    // 그 다음 절감액 큰 순
    const savA = calcSavings(a).amount;
    const savB = calcSavings(b).amount;
    return savB - savA;
  });
}

// 참여자 집계
export function aggregateParticipants(participants, groupBuyingId) {
  const list = (participants || []).filter(
    (p) => p.groupBuyingId === groupBuyingId && p.status !== 'cancelled'
  );
  const totalQuantity = list.reduce((s, p) => s + (Number(p.quantity) || 0), 0);
  const userCount = new Set(list.map((p) => p.userId)).size;
  return { totalQuantity, userCount, list };
}

// 사용자 본인이 특정 공동구매에 참여 중인지
export function findMyParticipation(participants, groupBuyingId, userId) {
  return (participants || []).find(
    (p) =>
      p.groupBuyingId === groupBuyingId &&
      p.userId === userId &&
      p.status !== 'cancelled'
  );
}

// 카테고리 옵션
export const GB_CATEGORIES = [
  { value: '야채', label: '🥬 야채/과일' },
  { value: '육류', label: '🥩 육류' },
  { value: '수산', label: '🐟 수산물' },
  { value: '공산품', label: '📦 공산품/소스' },
  { value: '주방용품', label: '🍳 주방용품' },
  { value: '기타', label: '기타' },
];

// 통화 포맷
export function formatKRW(n) {
  const num = Number(n) || 0;
  return num.toLocaleString('ko-KR');
}
